import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, useAuth, cacheGet, cacheSet } from "@/lib/api";
import { useLang, LANG_META } from "@/lib/i18n";
import { HERO_IMG, TRACK_IMAGES } from "@/lib/images";
import { toast } from "sonner";
import { BookOpen, Video, CheckCircle2, Award, ChevronRight, GraduationCap, Users, Lock, TrendingUp, Briefcase } from "lucide-react";

export default function Catalog() {
  const [tracks, setTracks] = useState(cacheGet("tracks") || []);
  const [enrollments, setEnrollments] = useState(cacheGet("enrollments") || []);
  const [catalog, setCatalog] = useState([]);
  const [certs, setCerts] = useState([]);
  const [apps, setApps] = useState([]);
  const { user } = useAuth();
  const { t, pick, langs } = useLang();

  useEffect(() => {
    api.get("/tracks").then((r) => { setTracks(r.data); cacheSet("tracks", r.data); }).catch(() => {});
    api.get("/me/enrollments").then((r) => { setEnrollments(r.data); cacheSet("enrollments", r.data); }).catch(() => {});
    api.get("/catalog").then((r) => setCatalog(r.data.filter((c) => !c.available))).catch(() => {});
    api.get("/me/certificates").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/me/applications").then((r) => setApps(r.data)).catch(() => {});
  }, []);

  const enrFor = (tid) => enrollments.find((e) => e.track_id === tid);
  const avgProgress = enrollments.length
    ? Math.round(enrollments.reduce((s, e) => {
        const tr = tracks.find((x) => x.id === e.track_id);
        return s + (e.certificate_id ? 100 : tr ? (e.lessons_completed.length / tr.lesson_count) * 100 : 0);
      }, 0) / enrollments.length)
    : 0;

  const kpis = [
    [enrollments.length, "Courses Enrolled", <BookOpen size={18} key="k1" />, "bg-teal-50 text-teal-700"],
    [`${avgProgress}%`, "Average Progress", <TrendingUp size={18} key="k2" />, "bg-sky-50 text-sky-700"],
    [certs.length, "Certificates Earned", <Award size={18} key="k3" />, "bg-amber-50 text-amber-700"],
    [apps.length, "Job Applications", <Briefcase size={18} key="k4" />, "bg-emerald-50 text-emerald-700"],
  ];

  return (
    <div className="space-y-6">
      <div className="relative rounded-3xl overflow-hidden shadow-md" data-testid="catalog-hero">
        <img src={HERO_IMG} alt="FSCD eye camp" className="w-full h-48 sm:h-60 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-950/85 via-teal-900/60 to-transparent" />
        <div className="absolute inset-0 p-5 sm:p-8 flex flex-col justify-end">
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold text-white">{t("welcomeBack")}, {user?.name?.split(" ")[0]}</h1>
          <p className="text-teal-100 mt-1 text-sm sm:text-base">{t("chooseTrack")}</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full"><GraduationCap size={14} /> {tracks.length} {t("tracks")}</span>
            <span className="inline-flex items-center gap-1.5 bg-amber-400/90 text-amber-950 text-xs font-semibold px-3 py-1.5 rounded-full"><Users size={14} /> {langs.map((l) => LANG_META[l].label).join(" + ")}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="student-kpis">
        {kpis.map(([v, l, icon, tint]) => (
          <div key={l} className="bg-white rounded-2xl border border-slate-200/80 p-4 flex items-center gap-3 shadow-sm">
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>{icon}</span>
            <div><p className="text-xl font-heading font-extrabold text-slate-900">{v}</p><p className="text-[11px] text-slate-500">{l}</p></div>
          </div>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tracks.map((tr) => {
          const enr = enrFor(tr.id);
          const pct = enr ? Math.round((enr.lessons_completed.length / tr.lesson_count) * 100) : 0;
          return (
            <Link key={tr.id} to={`/track/${tr.id}`} data-testid={`track-card-${tr.id}`}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-teal-300 transition-all flex flex-col">
              <div className="relative h-32">
                <img src={TRACK_IMAGES[tr.id] || HERO_IMG} alt={pick(tr.title)} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span className="absolute top-3 left-3 text-xs font-semibold uppercase tracking-wider text-teal-900 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full">
                  {tr.components.includes("video") ? t("theoryVideo") : t("theoryOnly")}
                </span>
                {enr?.certificate_id && <span className="absolute top-3 right-3 bg-amber-400 rounded-full p-1.5"><Award className="text-amber-950" size={16} /></span>}
              </div>
              <div className="p-5 flex flex-col gap-3 flex-1">
              <h2 className="font-heading font-bold text-lg text-slate-900 leading-snug">{pick(tr.title)}</h2>
              <p className="text-sm text-slate-500 flex-1">{pick(tr.desc)}</p>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <BookOpen size={14} /> {tr.lesson_count} {t("lessons")}
                {tr.components.includes("video") && <><Video size={14} className="ml-2" /> {t("videoPractical")}</>}
              </div>
              {enr ? (
                <div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-600 rounded-full transition-all" style={{ width: `${enr.certificate_id ? 100 : pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-slate-500">{enr.certificate_id ? <span className="flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 size={14} /> {t("completed")}</span> : `${pct}%`}</span>
                    <span className="text-teal-700 font-semibold flex items-center">{t("continueLearning")} <ChevronRight size={16} /></span>
                  </div>
                </div>
              ) : (
                <span className="text-teal-700 font-semibold text-sm flex items-center">{t("enroll")} <ChevronRight size={16} /></span>
              )}
              </div>
            </Link>
          );
        })}
      </div>
      {catalog.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-heading font-bold text-lg text-slate-900">Full Course Catalog — Coming Soon</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coming-soon-catalog">
            {catalog.map((c) => (
              <button key={c.id} onClick={() => toast.info(`${c.title} launches in Phase 2`)} data-testid={`catalog-course-${c.id}`}
                className="text-left bg-white/70 rounded-2xl border border-dashed border-slate-300 p-4 space-y-1.5 hover:border-red-300 transition-colors">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{c.vertical}</span>
                <p className="font-semibold text-slate-700 text-sm leading-snug">{c.title}</p>
                <p className="text-xs text-slate-400 flex items-center gap-1"><Lock size={11} /> {c.level} · {c.weeks} weeks {c.cert ? "· Certificate" : ""}</p>
              </button>
            ))}
          </div>
        </section>
      )}
      <p className="text-xs text-slate-400">{t("downloadNote")}</p>
    </div>
  );
}
